import React from 'react';
import { MoveDownRight, MoveRight, MoveUpRight } from 'lucide-react';
import TrendExplainer from 'src/shared/components/trend-explainer/TrendExplainer';
import { BaselineFallback } from 'src/shared/helpers/baselineFallback';
import { cn } from 'src/shared/helpers/utils';

type TrendPillProps = {
    trendValue: number;
    baselineYear: number;
    comparisonYear: number;
    example?: string;
    fallback?: BaselineFallback;
};

// For values that are already percentages, where a relative change is easy to misread.
export const percentageTrendExample = (metricName: string) =>
    `${metricName} is a percentage. If ${metricName.toLowerCase()} changes from 20% to 22%, this shows +10%, not +2%.`;

const trendNumberClasses = 'inline-block text-right tabular-nums';

const TrendPill = ({ trendValue, baselineYear, comparisonYear, example, fallback }: TrendPillProps) => {
    const colorInfo = trendColour(trendValue);

    return (
        <div
            className={cn(
                'flex gap-2 px-2 py-1 rounded-2xl text-xs items-center w-fit',
                fallback ? 'bg-yellow-0 ring-1 ring-inset ring-yellow-20' : 'bg-grey-0'
            )}
        >
            {colorInfo.arrowIcon}
            <div className='text-xs'>
                <span className={`${trendNumberClasses} ${colorInfo.colorClass}`}>{trendValue.toFixed(2)}%</span>
                <span className='text-grey-80'> since {baselineYear}</span>
            </div>
            <TrendExplainer baselineYear={baselineYear} comparisonYear={comparisonYear} example={example} fallback={fallback} />
        </div>
    );
};

export const trendColour = (trendValue: number) => {
    let colorClass = 'text-red-100';
    let arrowIcon = <MoveDownRight className='text-red-100 h-4 w-4' />;
    if (trendValue >= -0.1 && trendValue <= 0.1) {
        colorClass = 'text-orange-100';
        arrowIcon = <MoveRight className='text-orange-100 h-4 w-4' />;
    } else if (trendValue > 0.1) {
        colorClass = 'text-green-100';
        arrowIcon = <MoveUpRight className='text-green-100 h-4 w-4' />;
    }
    return {
        colorClass: colorClass,
        arrowIcon: arrowIcon,
    };
};

export default TrendPill;
